import * as React from 'react';
import { ErrorLogComponent } from '@cian/error-log-component';
import { VisitedOffersProvider, IVisitedOffersProviderProps } from '@cian/frontend-visited-offers-widget';
import { Provider as MicrofrontendProvider } from '@cian/mf-react';
import { UiKitToastsContextProvider } from '@cian/ui-kit/context';
import { UiKitRoot } from '@cian/ui-kit/root';
import { uaToDeviceType } from '@cian/ui-kit/utils';
import { Provider } from 'react-redux';

import { useHeaderDesktopHeightCssVariable, useOpenZhkCardUxfbEvent } from './hooks';
import { ContentWrapper } from '../../components/ContentWrapper';
import { GenplanNavContainer } from '../../components/Genplan/components/GenplanNavContainer';
import { Main } from '../../components/Main';
import { OrderWrapper } from '../../components/OrderWrapper';
import { IApplicationContext } from '../../types/applicationContext';
import { ICustomContextParams } from '../../types/ICustomContextParams';
import { TReduxStore } from '../../types/redux';
import { Context as ApplicationContext } from '../../utils/withApplicationContext';
import { About } from '../About';
import { AddressContainer } from '../Address';
import { AdvantagesContainer } from '../Advantages';
import { AeroPhotoTourEntryContainer } from '../AeroPhotoTourEntry';
import { AeroPhotoTourModalContainer } from '../AeroPhotoTourModal';
import { AgentsContainer } from '../Agents';
import { BasicInfoContainer } from '../BasicInfo';
import { BreadCrumbs } from '../BreadCrumbs/BreadCrumbs';
import { BuildersContainer } from '../Builder';
import { BuilderCallReasonsContainer } from '../BuilderCallReasons';
import { BuilderChatPopupContainer } from '../BuilderChatPopup';
import { BuilderNewbuildingsContainer } from '../BuilderNewbuildings';
import { CarouselContainer } from '../Carousel';
import { CompanySeoCard } from '../CompanySeoCard';
import { ConstructionProgressContainer } from '../ConstructionProgress';
import { ConsultantChatPopupContainer } from '../ConsultantChatPopupContainer';
import { EcologyContainer } from '../Ecology';
import { EscrowContainer } from '../Escrow';
import { FixedHeader } from '../FixedHeader';
import { FlatViewOrderTooltipContainer } from '../FlatViewOrderTooltip';
import { FloorPlanLoadable } from '../FloorPlan/FloorPlanLoadable';
import { GenplanLoadable } from '../Genplan/GenplanLoadable';
import { HousesByTurnSeoContainer } from '../HousesByTurnSeo';
import { ImageSources } from '../ImageSources';
import { InteriorDecorationContainer } from '../InteriorDecoration';
import { LastHopePopupContainer } from '../LastHopePopup';
import { MapSelectorContainer } from '../MapSelector';
import { MortgageBrokerBannerContainer } from '../MortgageBrokerBannerContainer';
import { MortgageMiniCalculatorWidget } from '../MortgageMiniCalculatorWidget';
import { MortgageSectionContainer } from '../MortgageSection';
import { NewbuildingCallbackWidgetProvider } from '../NewbuildingCallbackWidgetProvider';
import { NewbuildingReviewsTeaserContainer } from '../NewbuildingReviewsTeaser';
import { OffersBlockContainer } from '../OffersBlock';
import { OffersStatsContainer } from '../OffersStats';
import { Promo } from '../Promo';
import { PromoDetailsContainer } from '../PromoDetails';
import { PromosSectionContainer } from '../PromosSection';
import { RealtyValuationContainer } from '../RealtyValuation';
import { RecommendedNewbuildingsContainer } from '../RecommendedNewbuildings';
import { ReliabilityV2Container } from '../ReliabilityV2';
import { ScrollDown } from '../ScrollDown';
import { SeoMarkedListContainer } from '../SeoMarkedList';
import { SpecificationsContainer } from '../Specifications';
import { SpecificationsDetailedSeoContainer } from '../SpecificationsDetailedSeo';
import { TopControlsContainer } from '../TopControls';
import { Video } from '../Video';
import { VisitedOffersUpdater } from '../VisitedOffersUpdater';

export interface IApplicationProps extends IApplicationContext {
  reduxStore: TReduxStore;
  customContext: ICustomContextParams;
}

export const Application = ({
  config,
  customContext,
  httpApi,
  logger,
  reduxStore,
  microfrontendsRegistry,
  telemetry,
}: IApplicationProps) => {
  const context = React.useMemo(
    () => ({ config, httpApi, logger, microfrontendsRegistry, telemetry }),
    [config, httpApi, logger, microfrontendsRegistry, telemetry],
  );

  const { settings, enabled } = config.get<IVisitedOffersProviderProps>('audience.visitedOffers') || {};

  useHeaderDesktopHeightCssVariable();
  useOpenZhkCardUxfbEvent(config);

  return (
    <ErrorLogComponent logger={logger}>
      <ApplicationContext.Provider value={context}>
        <UiKitRoot deviceType={uaToDeviceType(customContext.userAgent)}>
          <UiKitToastsContextProvider>
            <Provider store={reduxStore}>
              <MicrofrontendProvider registry={microfrontendsRegistry}>
                <NewbuildingCallbackWidgetProvider>
                  <VisitedOffersProvider settings={settings} enabled={enabled}>
                    <VisitedOffersUpdater />
                    <FixedHeader />
                    <Main>
                      <CarouselContainer />
                      <BreadCrumbs />
                      <FlatViewOrderTooltipContainer />
                      <TopControlsContainer />
                      <BasicInfoContainer />
                      <ScrollDown />
                    </Main>
                    <ContentWrapper>
                      <Promo />
                      <AddressContainer />
                      <SpecificationsContainer />
                      <SpecificationsDetailedSeoContainer />
                      <BuilderCallReasonsContainer />
                      <HousesByTurnSeoContainer />
                      <GenplanNavContainer>
                        <GenplanLoadable />
                      </GenplanNavContainer>
                      <FloorPlanLoadable />
                      <OrderWrapper order={1}>
                        <About />
                      </OrderWrapper>
                      <OrderWrapper order={0}>
                        <OffersBlockContainer />
                        <PromosSectionContainer />
                        <MortgageMiniCalculatorWidget />
                        <MortgageSectionContainer />
                        <MortgageBrokerBannerContainer />
                        <RealtyValuationContainer />
                      </OrderWrapper>
                      <OrderWrapper order={2}>
                        <Video />
                        <AdvantagesContainer />
                        <NewbuildingReviewsTeaserContainer />
                        <MapSelectorContainer />
                        <AeroPhotoTourEntryContainer />
                        <EcologyContainer />
                        <InteriorDecorationContainer />
                        <BuildersContainer />
                        <AgentsContainer />
                        <BuilderNewbuildingsContainer />
                        <ConstructionProgressContainer />
                        <ReliabilityV2Container />
                        <EscrowContainer />
                        <OffersStatsContainer />
                        <RecommendedNewbuildingsContainer />
                        <ImageSources />
                        <BuilderChatPopupContainer />
                        <ConsultantChatPopupContainer />
                        <LastHopePopupContainer />
                        <AeroPhotoTourModalContainer />
                        <PromoDetailsContainer />
                        <CompanySeoCard />
                        <SeoMarkedListContainer />
                      </OrderWrapper>
                    </ContentWrapper>
                  </VisitedOffersProvider>
                </NewbuildingCallbackWidgetProvider>
              </MicrofrontendProvider>
            </Provider>
          </UiKitToastsContextProvider>
        </UiKitRoot>
      </ApplicationContext.Provider>
    </ErrorLogComponent>
  );
};
